import { IPageDimensions } from '../models/interfaces/page-dimensions.interface';
import { WordDataTypeEnum } from '../models/enums/word-data-type.enum';
import { Point2D, Polygon } from '../models/robots.model';
import { ISmartCanvasLayer, ISmartCanvasPolygon } from '../models/interfaces/smart-canvas.interface';
import { IFileLine, IFileTable, IFileWord } from '../models/interfaces/files.interface';

export class Calcs {
  public static levenshteinDistanceStrings(a: string, b: string): number {
    if (a.length === 0) {
      return b.length;
    }
    if (b.length === 0) {
      return a.length;
    }

    const matrix: number[][] = [];

    // increment along the first column of each row
    let i;
    for (i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }

    // increment each column in the first row
    let j;
    for (j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    // Fill in the rest of the matrix
    for (i = 1; i <= b.length; i++) {
      for (j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            Math.min(
              matrix[i][j - 1] + 1, // insertion
              matrix[i - 1][j] + 1
            )
          ); // deletion
        }
      }
    }

    return matrix[b.length][a.length];
  }

  public static pixelsPerInch(pageWidthInch: number, pageWidthPixels: number): number {
    return pageWidthPixels / pageWidthInch;
  }

  public static boundingBoxInchToPixels(boundingBoxInch: number[], pageWidthPixels: number, pageWidthInch: number): number[] {
    const pixelsPerInch = Calcs.pixelsPerInch(pageWidthInch, pageWidthPixels);
    return boundingBoxInch.map((inch) => inch * pixelsPerInch);
  }

  public static boundingBoxRelative(boundingBox: number[], pageDimension: IPageDimensions): number[] {
    const newBoundingBox = [];

    for (let i = 0; i < boundingBox.length; i = i + 2) {
      newBoundingBox[i] = boundingBox[i] * pageDimension.width;
      newBoundingBox[i + 1] = boundingBox[i + 1] * pageDimension.height;
    }

    return newBoundingBox;
  }

  public static boundingBoxPixelsToRelative(boundingbox: number[], pageDimension: IPageDimensions): number[] {
    const newBoundingBox = [];

    for (let i = 0; i < boundingbox.length; i = i + 2) {
      newBoundingBox[i] = boundingbox[i] / pageDimension.width;
      newBoundingBox[i + 1] = boundingbox[i + 1] / pageDimension.height;
    }

    return newBoundingBox;
  }

  public static boundingBoxInchToPixelsRelative(
    boundingBoxInch: number[],
    pageDimension: IPageDimensions,
    pageWidthPixels: number
  ): number[] {
    return this.boundingBoxInchToPixels(this.boundingBoxRelative(boundingBoxInch, pageDimension), pageWidthPixels, pageDimension.width);
  }

  public static polygonToBoundingBox(polygon: Polygon): number[] {
    const boundingBox = [];

    if (polygon) {
      for (let i = 0; i < polygon.coordinates.length; i++) {
        boundingBox.push(polygon.coordinates[i].x);
        boundingBox.push(polygon.coordinates[i].y);
      }
    }

    return boundingBox;
  }

  public static itemsInsideBoundingBox(polygonToVerify: number[], mainPolygon: number[], thresholdPercentage = 0): boolean {
    thresholdPercentage = 1 + thresholdPercentage;

    return (
      polygonToVerify[0] >= mainPolygon[0] * thresholdPercentage &&
      polygonToVerify[1] >= mainPolygon[1] * thresholdPercentage &&
      polygonToVerify[4] <= mainPolygon[4] * thresholdPercentage &&
      polygonToVerify[5] <= mainPolygon[5] * thresholdPercentage
    );
  }

  public static getClosestWordFromCentroid(words: IFileWord[] | IFileLine[], centroid: Point2D): IFileWord | IFileLine {
    let minDistance = Number.MAX_SAFE_INTEGER;
    let closestPolygon = words[0];

    for (let i = 0; i < words.length; i++) {
      const distance = Calcs.distanceBetweenPoints(new Polygon(words[i].boundingBoxRelative).centroid, centroid);

      if (distance < minDistance) {
        minDistance = distance;
        closestPolygon = words[i];
      }
    }

    return closestPolygon;
  }

  public static getClosestTableFromCentroid(tables: IFileTable[], centroid: Point2D): IFileTable {
    let minDistance = Number.MAX_SAFE_INTEGER;
    let closestPolygon: IFileTable = tables[0];

    for (let i = 0; i < tables.length; i++) {
      const distance = Calcs.distanceBetweenPoints(new Polygon(tables[i].boundingBoxRelative).centroid, centroid);

      if (distance < minDistance) {
        minDistance = distance;
        closestPolygon = tables[i];
      }
    }

    return closestPolygon;
  }

  public static getClosestSmartCanvasPolygonFromCentroid(
    polygons: ISmartCanvasPolygon[],
    centroid: Point2D,
    type?: WordDataTypeEnum
  ): ISmartCanvasPolygon {
    let minDistance = Number.MAX_SAFE_INTEGER;
    const detectedPolygons = type ? polygons.filter((x) => x.content.type === type) : polygons;
    let closestPolygon: ISmartCanvasPolygon = detectedPolygons[0];

    for (let i = 0; i < detectedPolygons.length; i++) {
      const distance = Calcs.distanceBetweenPoints(new Polygon(<number[]>detectedPolygons[i].poly.attrs.points).centroid, centroid);

      if (distance < minDistance) {
        minDistance = distance;
        closestPolygon = detectedPolygons[i];
      }
    }

    return closestPolygon;
  }

  public static orderSmartCanvasPolygonByCentroidDistance(polygons: ISmartCanvasPolygon[], centroid: Point2D): ISmartCanvasPolygon[] {
    return polygons.sort(
      (a, b) =>
        Calcs.distanceBetweenPoints(new Polygon(<number[]>a.poly.attrs.points).centroid, centroid) -
        Calcs.distanceBetweenPoints(new Polygon(<number[]>b.poly.attrs.points).centroid, centroid)
    );
  }

  public static distanceBetweenPoints(point1: Point2D, point2: Point2D): number {
    return Math.sqrt(Math.pow(point1.x - point2.x, 2) + Math.pow(point1.y - point2.y, 2));
  }

  public static absoluteToRelativePoints(points: number[], pages: IPageDimensions[], pageIndex: number, gutter: number): number[] {
    const relativePoints: number[] = points;

    pages.slice(1, pageIndex + 1).forEach((page) => {
      relativePoints[0] = relativePoints[0] - page.width - gutter;
      relativePoints[2] = relativePoints[2] - page.width - gutter;
      relativePoints[4] = relativePoints[4] - page.width - gutter;
      relativePoints[6] = relativePoints[6] - page.width - gutter;
    });

    return relativePoints;
  }

  public static getPolygonsFromCanvasArea(
    area: number[],
    pageIndex: number,
    canvasLayer: ISmartCanvasLayer,
    centroid?: Point2D
  ): ISmartCanvasPolygon[] {
    if (area) {
      const wordsInsideSelectedArea = canvasLayer.polygons.filter(
        (poly) => poly.pageIndex === pageIndex && Calcs.itemsInsideBoundingBox(<number[]>poly.poly.attrs.points, area)
      );

      return centroid ? Calcs.orderSmartCanvasPolygonByCentroidDistance(wordsInsideSelectedArea, centroid) : wordsInsideSelectedArea;
    }

    return [];
  }
}
